import React, { useState, useEffect } from 'react'
import { 
  Bot, 
  Activity, 
  Clock, 
  AlertCircle, 
  Play, 
  Pause, 
  RefreshCw,
  Search,
  Filter,
  ChevronRight,
  Zap,
  BookOpen,
  X
} from 'lucide-react'
import { apiFetch } from '../config'

const Agents = () => {
  const [agents, setAgents] = useState([])
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [selectedAgent, setSelectedAgent] = useState(null)
  const [activating, setActivating] = useState(false)

  useEffect(() => {
    fetchAgents()
    fetchCategories()
  }, [])

  const fetchAgents = async () => {
    try {
      setLoading(true)
      const response = await apiFetch("/agency-agents")
      const data = await response.json()
      setAgents(data.agents || [])
      setLoading(false)
    } catch (error) {
      console.error('Failed to fetch agents:', error)
      setLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await apiFetch("/agency-agents/categories")
      const data = await response.json()
      setCategories(data.categories || [])
    } catch (error) {
      console.error('Failed to fetch categories:', error)
    }
  }

  const activateAgent = async (agentId) => {
    try {
      setActivating(true)
      const response = await apiFetch(`/agency-agents/${agentId}/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ context: 'Manual activation from dashboard' })
      })
      
      if (response.ok) {
        // Refresh agents to get updated status
        fetchAgents()
      }
      setActivating(false)
    } catch (error) {
      console.error('Failed to activate agent:', error)
      setActivating(false)
    }
  }

  const filteredAgents = agents.filter(agent => {
    const matchesCategory = selectedCategory === 'all' || agent.category === selectedCategory
    const matchesSearch = !searchQuery || 
      agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.description?.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesCategory && matchesSearch
  })

  const getCategoryIcon = (category) => {
    const icons = {
      'engineering': '⚙️',
      'marketing': '📢',
      'design': '🎨',
      'sales': '💼',
      'product': '📱',
      'testing': '🧪',
      'support': '🎧',
      'strategy': '🎯',
      'specialized': '🔧',
      'game-development': '🎮',
      'spatial-computing': '🥽',
      'academic': '📚',
      'paid-media': '💰'
    }
    return icons[category] || '🤖'
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500'
      case 'idle': return 'bg-yellow-500'
      case 'offline': return 'bg-gray-500'
      default: return 'bg-blue-500'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    )
  }

  return (
    <div className="h-full flex">
      {/* Sidebar - Categories */}
      <div className="w-64 bg-slate-800 border-r border-slate-700 p-4 overflow-y-auto">
        <div className="flex items-center gap-2 mb-6">
          <Bot className="w-5 h-5 text-indigo-400" />
          <h2 className="font-semibold text-white">Categories</h2>
        </div>
        
        <button
          onClick={() => setSelectedCategory('all')}
          className={`w-full text-left px-4 py-3 rounded-lg mb-2 flex items-center justify-between transition-colors ${
            selectedCategory === 'all' 
              ? 'bg-indigo-600 text-white' 
              : 'hover:bg-slate-700 text-slate-300'
          }`}
        >
          <span>All Agents</span>
          <span className="text-sm bg-slate-700 px-2 py-1 rounded-full">{agents.length}</span>
        </button>

        {categories.map((cat) => (
          <button
            key={cat.category}
            onClick={() => setSelectedCategory(cat.category)}
            className={`w-full text-left px-4 py-3 rounded-lg mb-2 flex items-center justify-between transition-colors ${
              selectedCategory === cat.category 
                ? 'bg-indigo-600 text-white' 
                : 'hover:bg-slate-700 text-slate-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <span>{getCategoryIcon(cat.category)}</span>
              <span className="capitalize">{cat.category.replace(/-/g, ' ')}</span>
            </span>
            <span className="text-sm bg-slate-700 px-2 py-1 rounded-full">{cat.count}</span>
          </button>
        ))}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-white">AI Agents</h1>
              <p className="text-slate-400 mt-1">
                {selectedCategory === 'all' 
                  ? `All ${agents.length} autonomous agents` 
                  : `${filteredAgents.length} agents in ${selectedCategory}`}
              </p>
            </div>
            <button 
              onClick={fetchAgents}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search agents by name or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
            />
          </div>
        </div>

        {/* Agents Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredAgents.map((agent) => (
              <div 
                key={agent.id}
                className="bg-slate-800 border border-slate-700 rounded-lg p-5 hover:border-indigo-500 transition-colors cursor-pointer"
                onClick={() => setSelectedAgent(agent)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center text-xl">
                      {getCategoryIcon(agent.category)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{agent.name}</h3>
                      <span className="text-xs text-slate-400 capitalize">{agent.category.replace(/-/g, ' ')}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${getStatusColor(agent.status)}`} />
                    <span className="text-xs text-slate-400 capitalize">{agent.status}</span>
                  </div>
                </div>
                
                <p className="text-sm text-slate-400 mb-4 line-clamp-2">
                  {agent.description || 'No description available'}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <span>Activated: {agent.activation_count || 0} times</span>
                  </div>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      activateAgent(agent.id)
                    }}
                    disabled={activating || agent.status === 'active'}
                    className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 disabled:text-slate-500 rounded text-white text-sm transition-colors"
                  >
                    <Zap className="w-4 h-4" />
                    {agent.status === 'active' ? 'Active' : 'Activate'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredAgents.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Bot className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No agents found matching your criteria</p>
            </div>
          )}
        </div>
      </div>

      {/* Agent Detail Modal */}
      {selectedAgent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center text-2xl">
                  {getCategoryIcon(selectedAgent.category)}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{selectedAgent.name}</h2>
                  <span className="text-sm text-slate-400 capitalize">{selectedAgent.category.replace(/-/g, ' ')}</span>
                </div>
              </div>
              <button 
                onClick={() => setSelectedAgent(null)}
                className="p-2 hover:bg-slate-700 rounded-lg"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-300 mb-2">Description</h3>
                <p className="text-slate-400">{selectedAgent.description || 'No description available'}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-700/50 p-4 rounded-lg">
                  <p className="text-sm text-slate-400">Status</p>
                  <p className="text-white capitalize">{selectedAgent.status}</p>
                </div>
                <div className="bg-slate-700/50 p-4 rounded-lg">
                  <p className="text-sm text-slate-400">Activation Count</p>
                  <p className="text-white">{selectedAgent.activation_count || 0}</p>
                </div>
              </div>
              
              {selectedAgent.skills && JSON.parse(selectedAgent.skills).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-300 mb-2">Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {JSON.parse(selectedAgent.skills).map((skill, i) => (
                      <span key={i} className="px-3 py-1 bg-slate-700 rounded-full text-sm text-slate-300">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => activateAgent(selectedAgent.id)}
                  disabled={activating || selectedAgent.status === 'active'}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 disabled:text-slate-500 rounded-lg text-white font-medium transition-colors"
                >
                  <Zap className="w-5 h-5" />
                  {selectedAgent.status === 'active' ? 'Already Active' : 'Activate Agent'}
                </button>
                
                {selectedAgent.content && (
                  <button
                    onClick={() => alert('Full documentation would open here')}
                    className="flex items-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-white transition-colors"
                  >
                    <BookOpen className="w-5 h-5" />
                    Docs
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Agents
